import React, { useState } from 'react';
import { Card, Table, Input, Select, DatePicker, Row, Col, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import workOrderService, { type RouteSheetRegisterRow } from '@/services/workOrder.service';
import { WORK_ORDER_EXECUTION_SCOPES } from '@/services/workOrder.service';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const RouteSheetRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState('');
  const [project, setProject] = useState('');
  const [executionScope, setExecutionScope] = useState<string>('');
  const [woNumber, setWoNumber] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([] as any);

  const filters = {
    customer_name: customer || undefined,
    project_name: project || undefined,
    execution_scope: executionScope || undefined,
    work_order_id: undefined as string | undefined,
    from_date: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    to_date: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
  };

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['route-sheet-register', filters],
    queryFn: () => workOrderService.getRouteSheetRegister(filters),
  });

  const filteredRows = woNumber.trim()
    ? rows.filter((r) => r.work_order_number?.toLowerCase().includes(woNumber.trim().toLowerCase()))
    : rows;

  const columns: ColumnsType<RouteSheetRegisterRow> = [
    {
      title: 'Route sheet no',
      dataIndex: 'route_sheet_no',
      key: 'route_sheet_no',
      render: (v: string, r) => (
        <a onClick={() => navigate(`/mfg/work-orders/${r.work_order_id}/route-sheet`)}>{v}</a>
      ),
    },
    { title: 'Rev', dataIndex: 'revision_no', key: 'revision_no', width: 56 },
    {
      title: 'Work order',
      dataIndex: 'work_order_number',
      key: 'work_order_number',
      render: (v: string, r) => (
        <a onClick={() => navigate(`/mfg/work-orders/${r.work_order_id}`)}>{v}</a>
      ),
    },
    { title: 'Customer', dataIndex: 'customer_name', key: 'customer_name' },
    { title: 'Project', dataIndex: 'project_name', key: 'project_name' },
    { title: 'Scope', dataIndex: 'execution_scope', key: 'execution_scope', width: 140 },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true },
    {
      title: 'Generated',
      dataIndex: 'generated_at',
      key: 'generated_at',
      width: 160,
      render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY HH:mm') : '—'),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 16 }}>
        Route sheet register
      </Title>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={6}>
            <Input placeholder="Work order no" value={woNumber} onChange={(e) => setWoNumber(e.target.value)} allowClear />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input placeholder="Customer" value={customer} onChange={(e) => setCustomer(e.target.value)} allowClear />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input placeholder="Project" value={project} onChange={(e) => setProject(e.target.value)} allowClear />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              placeholder="Execution scope"
              style={{ width: '100%' }}
              value={executionScope || undefined}
              onChange={(v) => setExecutionScope(v ?? '')}
              options={WORK_ORDER_EXECUTION_SCOPES.map((s) => ({ value: s, label: s }))}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <RangePicker style={{ width: '100%' }} value={dateRange} onChange={(v) => setDateRange(v as any)} />
          </Col>
        </Row>
      </Card>
      <Card>
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={filteredRows}
          scroll={{ x: 900 }}
          pagination={{ pageSize: 25, showSizeChanger: true }}
        />
      </Card>
    </div>
  );
};

export default RouteSheetRegisterPage;
